import { useState } from 'react';
import { Video, Radio, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { 
  useBroadcast, 
  VideoPreview, 
  StreamControls, 
  StreamSettings, 
  StreamInfo, 
  StreamTips 
} from '../components/broadcast';

export default function BroadcastStudioPage() {
  const { isAdmin } = useAuth();
  const [streamTitle, setStreamTitle] = useState('Live Stream');
  const [sourceType, setSourceType] = useState('camera');
  
  const {
    isStreaming,
    streamId,
    viewerCount,
    videoEnabled,
    audioEnabled,
    localVideoRef,
    localStreamRef,
    startStream,
    stopStream,
    toggleVideo,
    toggleAudio
  } = useBroadcast();

  if (!isAdmin) {
    return (
      <div className="main-content pt-24">
        <div className="container py-12">
          <div className="empty-state glass-card py-16">
            <Video className="empty-state-icon w-16 h-16" />
            <h3 className="text-xl font-semibold mb-2">Admin Access Required</h3>
            <p className="text-gray-400">Only admins can access the broadcast studio</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content pt-24" data-testid="broadcast-studio">
      <div className="container py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              isStreaming ? 'bg-red-500 animate-pulse' : 'bg-gradient-to-br from-red-500 to-pink-500'
            }`}>
              <Radio className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Broadcast Studio</h1>
              <p className="text-gray-400">
                {isStreaming ? 'You are LIVE!' : 'Start streaming to your fans'}
              </p>
            </div>
          </div>
          
          {isStreaming && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg">
                <Users className="w-5 h-5 text-cyan-400" />
                <span className="font-bold">{viewerCount}</span>
                <span className="text-gray-400">watching</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-red-500 rounded-lg animate-pulse">
                <span className="w-2 h-2 rounded-full bg-white"></span>
                <span className="font-bold">LIVE</span>
              </div>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Preview */}
          <div className="lg:col-span-2">
            <div className="glass-card p-6">
              <VideoPreview 
                videoRef={localVideoRef} 
                localStream={localStreamRef.current} 
                isStreaming={isStreaming} 
              />
              
              <StreamControls
                isStreaming={isStreaming}
                videoEnabled={videoEnabled}
                audioEnabled={audioEnabled}
                onToggleVideo={toggleVideo}
                onToggleAudio={toggleAudio}
                onStartStream={() => startStream(streamTitle, sourceType)}
                onStopStream={stopStream}
              />
            </div>
          </div>

          {/* Settings */}
          <div className="space-y-6">
            <StreamSettings
              streamTitle={streamTitle}
              setStreamTitle={setStreamTitle}
              sourceType={sourceType}
              setSourceType={setSourceType}
              isStreaming={isStreaming}
            />

            {isStreaming && (
              <StreamInfo viewerCount={viewerCount} streamId={streamId} />
            )}

            <StreamTips />
          </div>
        </div>
      </div>
    </div>
  );
}
